import React, { useState, useEffect } from "react";
import emailjs from '@emailjs/browser';
import { Stars, Send } from "lucide-react";

const FeedbackPage = () => {
  const [content, setContent] = useState(null);
  const [language, setLanguage] = useState(null);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    emailjs.init("X82T51hdYcgnLZEIQ");
    
    const fetchContent = async () => {
      try {
        const response = await fetch("/data.json");
        const data = await response.json();
        
        const browserLang = navigator.language.toLowerCase();
        let detectedLang = browserLang.split("-")[0];
        
        // Ensure detected language is one of our supported languages
        if (!data[detectedLang]) {
          detectedLang = "en";
        }
        
        setLanguage(detectedLang);
        setContent(data);
      } catch (error) {
        console.error("Error loading content:", error);
      }
    };

    fetchContent();
  }, []);

  if (!content || !language) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const texts = content[language];
    
    try {
      const templateParams = {
        to_email: texts.submit_address,
        message: message,
        from_name: 'qrcode_www.classicskischool.cz',
        reply_to: texts.submit_address,
        subject: texts.title,
        language: language
      };

      const result = await emailjs.send(
        "service_qx2h6xk",
        "template_c23juul",
        templateParams,
        "X82T51hdYcgnLZEIQ"
      );

      if (result.status === 200) {
        setSubmitted(true);
        setMessage("");
      } else {
        throw new Error('Failed to send email');
      }
    } catch (err) {
      console.error('Failed to send email:', err);
      setError(texts.error_message || "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const texts = content[language];
  const { labels: l, company: c } = texts.contact;
  const address = `${c.address.street}, ${c.address.pobox}, ${c.address.city}, ${c.address.country}`;

  // Get the review address for the detected language
  const reviewAddress = texts.review_address;

  return (
    <div className="min-h-screen bg-black text-white py-3 px-3 sm:px-4 lg:px-6">
      <div className="max-w-md mx-auto bg-black/80 rounded-lg shadow-xl overflow-hidden border border-gray-800">
        {texts.logo_path && (
          <div className="p-4">
            <img
              src={texts.logo_path}
              alt="Classic Ski School Logo"
              className="h-14 object-contain mx-auto"
            />
          </div>
        )}
        
        <div className="p-4">
          <h1 className="text-xl font-bold text-center text-white mb-3">
            {texts.title}
          </h1>

          <div className="space-y-3">
            <div className="text-center">
              <p className="text-base text-gray-300 mb-3">{texts.satisfied}</p>
              <button
                className="w-full bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors font-bold flex items-center justify-center space-x-2"
                onClick={() => window.open(reviewAddress, "_blank")}
              >
                <Stars className="w-4 h-4" />
                <span>{texts.review_button}</span>
              </button>
            </div>

            <div className="border-t border-gray-700 pt-4">
              <h3 className="text-base font-medium text-white mb-3">
                {texts.not_satisfied}
              </h3>
              
              {!submitted ? (
                <form onSubmit={handleSubmit}>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={texts.message_placeholder}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-18 text-white placeholder-gray-400"
                    required
                    disabled={isSubmitting}
                  />
                  {error && (
                    <div className="mt-2 text-red-500 text-sm">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="mt-3 w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    <Send className="w-4 h-4" />
                    <span>{isSubmitting ? '...' : texts.submit_button}</span>
                  </button>
                </form>
              ) : (
                <div className="text-center text-yellow-400 font-medium py-3">
                  {texts.thank_you}
                </div>
              )}
            </div>
            
            <div className="border-t border-gray-700 pt-3">
              <p className="text-xs text-gray-300 leading-relaxed">
                {l.provider} {c.name} <br />
                {l.address}: {address} <br />
                {l.business_id}: {c.business_id}, {' '}
                {l.vat}: {c.vat}, {' '}
                {l.email}: {' '}
                <a href={`mailto:${c.email}`} className="hover:text-yellow-400">
                  {c.email}
                </a>, {' '}
                {l.web}: {' '}
                <a 
                  href={`https://${c.web}`}
                  target="_blank"
                  rel="noopener noreferrer" 
                  className="hover:text-yellow-400"
                >
                  {c.web}
                </a>
                {c.phones.map((phone, index) => (
                  <span key={phone}>
                    , {l.tel}: {' '}
                    <a
                      href={`tel:${phone.replace(/\s+/g, '')}`}
                      className="hover:text-yellow-400"
                    >
                      {phone}
                    </a>
                  </span>
                ))}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;